// src/components/Thread.js — threads in channels (25 Oct 2026; backend #217).
//
//   ReplyCount  — on a root message in the channel: "12 replies · 2h ago",
//                 or a quiet "Reply" link when there are none yet. Opens the panel.
//   ThreadPanel — the root at the top, replies oldest first, a reply box at the
//                 bottom. Polls while open. Replying to a reply still lands in
//                 the root's thread (one level deep, on purpose).
//
// The channel shows roots only (the backend filters replies out), so a long
// debate no longer buries the channel — it lives here.

import { useState, useEffect, useCallback, useRef } from 'react';
import { API_BASE as API } from '../utils/apiBase';

const C = { accent: '#14b8a6', text: '#d4d4d8', dim: '#a1a1aa', muted: '#52525b', faint: '#3f3f46', bad: '#f87171' };
const btn = (color = C.accent) => ({ padding: '8px 14px', borderRadius: 8, border: `1px solid ${color}55`, background: `${color}14`, color, fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: "'Inter', sans-serif" });

async function call(path, headers, opts = {}) {
  const res = await fetch(`${API}${path}`, { ...opts, headers: { ...headers(), ...(opts.headers || {}) } });
  const d = await res.json().catch(() => ({}));
  if (!res.ok || d.success === false) throw new Error(d.message || `HTTP ${res.status}`);
  return d;
}

function ago(iso) {
  if (!iso) return '';
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

// ── "N replies" under a root ─────────────────────────────────────────────────
export function ReplyCount({ msg, onOpen }) {
  const n = msg.replyCount || 0;
  return (
    <button onClick={() => onOpen(msg)} title={n ? 'Open this thread' : 'Reply in a thread'}
      style={{ marginTop: 4, padding: 0, border: 'none', background: 'none', cursor: 'pointer', fontSize: 11, fontFamily: "'Inter', sans-serif", color: n ? C.accent : C.faint }}>
      {n ? `💬 ${n} ${n === 1 ? 'reply' : 'replies'} · ${ago(msg.lastReplyAt)}` : 'Reply'}
    </button>
  );
}

// ── One message inside the panel ─────────────────────────────────────────────
function Line({ m, isOwn, root }) {
  const time = new Date(m.createdAt).toLocaleString('en-AU', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
  return (
    <div style={{ display: 'flex', gap: 10, padding: root ? '10px 0 12px' : '6px 0', borderBottom: root ? '1px solid rgba(255,255,255,0.06)' : 'none', marginBottom: root ? 6 : 0 }}>
      <div style={{ width: 28, height: 28, borderRadius: 14, background: 'rgba(20,184,166,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 600, color: C.accent, flexShrink: 0 }}>
        {(m.user?.name || '?')[0].toUpperCase()}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'baseline' }}>
          <span style={{ fontSize: 12, fontWeight: 600, color: isOwn ? C.accent : C.text }}>{m.user?.name || 'Unknown'}</span>
          <span style={{ fontSize: 10, color: C.faint }}>{time}</span>
        </div>
        <div style={{ fontSize: 13, color: root ? C.text : C.dim, lineHeight: 1.5, marginTop: 2, wordBreak: 'break-word', whiteSpace: 'pre-wrap' }}>{m.content}</div>
      </div>
    </div>
  );
}

// ── The panel ────────────────────────────────────────────────────────────────
export function ThreadPanel({ rootId, headers, userId, deep, onClose, onReplied }) {
  const minChars = deep && deep.enabled ? deep.minChars || 200 : 0;
  const [root, setRoot] = useState(null);
  const [replies, setReplies] = useState([]);
  const [err, setErr] = useState('');
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const endRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const d = await call(`/api/signal/messages/${rootId}/thread`, headers);
      setRoot(d.data.root); setReplies(d.data.replies || []); setErr('');
    } catch (e) { setErr(e.message); }
  }, [rootId, headers]);

  useEffect(() => { load(); const t = setInterval(load, 5000); return () => clearInterval(t); }, [load]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [replies.length]);

  async function send() {
    const content = text.trim();
    if (!content || busy || content.length < minChars) return;
    setBusy(true); setErr('');
    try {
      const d = await call(`/api/signal/messages/${rootId}/thread`, headers, { method: 'POST', body: JSON.stringify({ content }) });
      setReplies((rs) => [...rs, d.data]); setText('');
      onReplied && onReplied(rootId, replies.length + 1, d.data.createdAt);
    } catch (e) { setErr(e.message); }
    setBusy(false);
  }

  return (
    <div style={{ width: 'min(380px, 100vw)', borderLeft: '1px solid rgba(255,255,255,0.06)', background: 'rgba(14,14,16,0.95)', display: 'flex', flexDirection: 'column', height: '100dvh', flexShrink: 0 }}>
      <div style={{ height: 52, display: 'flex', alignItems: 'center', padding: '0 16px', gap: 10, borderBottom: '1px solid rgba(255,255,255,0.04)', flexShrink: 0 }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: C.text }}>Thread</div>
          <div style={{ fontSize: 10, color: C.muted }}>{replies.length} {replies.length === 1 ? 'reply' : 'replies'}</div>
        </div>
        <button onClick={onClose} style={{ ...btn(C.muted), padding: '4px 10px' }}>Close</button>
      </div>

      <div style={{ flex: 1, overflowY: 'auto', padding: '8px 16px' }}>
        {!root && !err && <div style={{ fontSize: 12, color: C.muted }}>Loading…</div>}
        {err && !root && <div style={{ fontSize: 12, color: C.bad }}>{err}</div>}
        {root && <Line m={root} isOwn={String(root.user?._id || root.user) === String(userId)} root />}
        {root && replies.length === 0 && <div style={{ fontSize: 12, color: C.muted, padding: '8px 0' }}>No replies yet. Yours would be the first.</div>}
        {replies.map((m) => <Line key={m._id} m={m} isOwn={String(m.user?._id || m.user) === String(userId)} />)}
        <div ref={endRef} />
      </div>

      <div style={{ padding: '12px 16px', borderTop: '1px solid rgba(255,255,255,0.04)', flexShrink: 0 }}>
        {err && root && <div style={{ fontSize: 11, color: C.bad, marginBottom: 6 }}>{err}</div>}
        <textarea value={text} onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
          placeholder={minChars ? `Reply in thread — at least ${minChars} characters here. Say more, or say nothing.` : 'Reply in thread… (Shift+Enter for a new line)'}
          style={{ width: '100%', minHeight: 56, padding: '10px 12px', borderRadius: 10, border: '1px solid rgba(255,255,255,0.08)', background: 'rgba(39,39,42,0.5)', color: '#e4e4e7', fontSize: 13, outline: 'none', fontFamily: "'Inter', sans-serif", resize: 'vertical', boxSizing: 'border-box' }} />
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 6 }}>
          <span style={{ fontSize: 10, color: minChars && text.trim().length < minChars ? C.dim : C.faint }}>
            {minChars ? `${text.trim().length} / ${minChars}` : (root && root.user?.name ? `${root.user.name} will be told.` : '')}
          </span>
          <button onClick={send} disabled={busy || !text.trim() || text.trim().length < minChars} style={{ ...btn(), opacity: !text.trim() || text.trim().length < minChars ? 0.4 : 1 }}>Reply</button>
        </div>
      </div>
    </div>
  );
}

// ── Deep threads: the badge and the moderator's switch (backend #218) ────────
export function DeepBadge({ minChars }) {
  return (
    <span title={`Deep threads: replies need at least ${minChars} characters; reactions are off.`}
      style={{ fontSize: 9, padding: '3px 10px', borderRadius: 6, background: 'rgba(167,139,250,0.08)', color: '#a78bfa', border: '1px solid rgba(167,139,250,0.2)' }}>
      deep · {minChars}+
    </span>
  );
}

export function DeepToggle({ channel, headers, onChanged }) {
  const on = !!channel.deepThreads?.enabled;
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  async function flip() {
    setBusy(true); setErr('');
    try {
      const body = on ? { deepThreads: { enabled: false } } : { deepThreads: { enabled: true, minChars: channel.deepThreads?.minChars || 200 } };
      const d = await call(`/api/signal/channels/${channel.slug}/settings`, headers, { method: 'PUT', body: JSON.stringify(body) });
      onChanged && onChanged(d.data.deepThreads);
    } catch (e) { setErr(e.message); }
    setBusy(false);
  }
  return (
    <button onClick={flip} disabled={busy} title={on ? 'Turn deep threads off: short replies and reactions come back' : 'Turn deep threads on: replies need 200+ characters, reactions off. The room is told.'}
      style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 8, border: '1px solid rgba(167,139,250,0.2)', background: on ? 'rgba(167,139,250,0.14)' : 'rgba(167,139,250,0.06)', color: '#a78bfa', fontSize: 12, cursor: 'pointer', fontFamily: "'Inter', sans-serif" }}>
      🧠 Deep threads {on ? 'on' : 'off'}{err ? ` — ${err}` : ''}
    </button>
  );
}
